import {objectFromEntries} from '@benchling/js-utils';
import moment from 'moment';

import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {ObjectType} from 'app-core/framework/CoreObjectConstants';
import {getIdPrefixForObjectType} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {assert} from 'Asserts';
import {DataFrameSpec} from 'data-frames/DataFrameTypes';
import columnTypeToTypeDef from 'data-frames/util/columnTypeToTypeDef';
import ColumnTypeValidationException from 'data-frames/util/ColumnTypeValidationException';
import findColumn from 'data-frames/util/findColumn';
import getUserColumns from 'data-frames/util/getUserColumns';
import validateColumnType, {ColumnTypeValidationOptions} from 'data-frames/util/validateColumnType';
import isValidBenchlingTypeValue from 'data-types/util/isValidBenchlingTypeValue';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {USER_EDIT_DATE_FORMAT} from 'util/DateConstants';
import {JSONValue, assertNever} from 'util/Types';

import {PIPELINE_STEP_SHAPES} from './GeneratedPipelineStepShapes';
import {UNSET_CONFIG_VARIABLE} from './PipelineConfigConstants';
import {PipelineStepConfig} from './PipelineTypes';

export const HEX_COLOR_PATTERN = '^#[A-Fa-f0-9]{6}$';

export interface StepDataShape {
  kind: PipelineStepDataKind;
  isMulti: boolean;
  isRequired: boolean;
  systemName: string;
  displayName: string;
}

export type StepConfigShape = Record<string, PipelineConfigJsonSpec>;

/**
 * Which kinds of plate a step type can act on.
 *
 * Pipelines shows a single plate type, but some plates mirror a real inventory plate and some are
 * pipeline-only. Keep in sync with `PlateTargetEligibility` in
 * `benchling/pipelines/step_type_library/step_shapes.py`. When a step shape omits the field,
 * consumers treat it as `SCRATCH_ONLY`.
 */
export enum PlateTargetEligibility {
  ANY = 'ANY',
  INVENTORY_ONLY = 'INVENTORY_ONLY',
  SCRATCH_ONLY = 'SCRATCH_ONLY',
}

export interface PipelineStepShape {
  stepType: PipelineStepType;
  isDeprecated: boolean;
  version: number;
  plateTargetEligibility?: PlateTargetEligibility;
  inputDataShapes: Record<string, StepDataShape>;
  outputDataShapes: Record<string, StepDataShape>;
  config: StepConfigShape | null;
}

export interface EnumOptions {
  value: string | number;
  displayName?: string;
  iconName?: BenchlingIconName;
  svgImagePath?: string;
}

// Keep in sync with `benchling/pipelines/step_type_library/manifests/types/config_specs.yaml` on the back-end
export type PipelineConfigJsonSpec = {required: boolean} & (
  | {kind: 'string'; value?: string; pattern?: string}
  | {kind: 'number'}
  | {kind: 'integer'; minValue?: number; maxValue?: number}
  | {kind: 'boolean'}
  | {kind: 'color'; pattern?: string}
  | {kind: 'date'}
  | {kind: 'enum'; choices: ReadonlyArray<string> | ReadonlyArray<number> | ReadonlyArray<EnumOptions>}
  | {
      kind: 'columnReference';
      inputDataName: string;
      validationOptions: ColumnTypeValidationOptions | null;
      // When true, a value referencing a column that is absent from the input is considered valid.
      // Used for presentational config (e.g. TABLE_VIEW displayOptions) that may reference columns
      // that only exist in some runs.
      allowMissingColumn?: boolean;
    }
  | {kind: 'columnValue'; columnSystemName: string; inputDataName: string; inputDataIndex: number}
  | {kind: 'tableReference'; inputDataName: string; inputDataIndex: number}
  | {kind: 'array'; elementSpec: PipelineConfigJsonSpec; minItems?: number; maxItems?: number}
  | {kind: 'shape'; fields: Record<string, PipelineConfigJsonSpec>}
  | {kind: 'columnMapping'; fields: Record<string, PipelineConfigJsonSpec>}
  | {
      kind: 'schemaFields';
      rowSourceField: string;
      // Whether a resolved field's requiredness is surfaced in the grid (create: true, update: false).
      surfaceFieldRequiredness?: boolean;
      // Rows the backend resolves for the current selection at editor-load; absent until hydrated.
      __resolvedRows?: ReadonlyArray<{
        systemName: string;
        displayName: string;
        fieldType?: string | null;
        required?: boolean;
      }>;
      // True only when the backend resolved the source; absent from servers that cannot distinguish an
      // unresolved source from a resolved one with no fields.
      __rowsResolved?: boolean;
      __schemaInaccessible?: boolean;
    }
  | {
      kind: 'taggedUnion';
      tagKey: string;
      choices: ReadonlyArray<Extract<PipelineConfigJsonSpec, {kind: 'shape'}>>;
    }
  | {
      kind: 'domain';
      types: Array<ObjectType>;
      schemaIds?: Array<string> | null;
      schemaInterfaceIds?: Array<string> | null;
      // Object types the step can actually route, narrowing the schema picker below `types` (e.g. an
      // ENTITY_SCHEMA picker restricted to the entity subtypes the step supports). Stamped by the
      // backend at editor-load; absent means no narrowing.
      routableTypes?: Array<ObjectType> | null;
    }
  | {kind: 'anything'}
);

/**
 * Get the typescript shape of the given spec kind
 *
 * @example GetPipelineConfigSpecType<'enum'> -> {kind: 'enum'; choices: ReadonlyArray<string> | ReadonlyArray<number>}
 */
export type GetPipelineConfigSpecType<T extends PipelineConfigJsonSpec['kind']> = Extract<
  PipelineConfigJsonSpec,
  {kind: T}
>;

export function isStringSpec(
  spec: PipelineConfigJsonSpec
): spec is Extract<PipelineConfigJsonSpec, {kind: 'string'}> {
  return spec.kind === 'string';
}

/**
 * Return whether the input value is valid for the given Pipeline Config JSON spec.
 *
 * @example
 * ```
 * isValueValid('foo', {kind: 'string', required: true}, inputDataFrames) -> true
 * isValueValid(null, {kind: 'string', required: true}, inputDataFrames) -> false
 * isValueValid('hi', {kind: 'columnReference', ...}, dataFrameWithHiColumnSpec) -> true
 * ```
 *
 * @param inputDataFrames Map of input step data names to data frames' columns. See getInputStepDatasRecord.
 * @see examples in StepShapeUtils-test.ts
 */
export function isValueValid(
  value: JSONValue,
  spec: PipelineConfigJsonSpec,
  inputDataFrames: Record<string, Array<DataFrameSpec>>
): boolean {
  if (!spec.required && value == null) {
    return true;
  }
  // Template steps use UNSET as a placeholder for values filled at apply time. The backend
  // (step_config_shapes.py) accepts UNSET for any field, so we match that here.
  if (value === UNSET_CONFIG_VARIABLE) {
    return true;
  }
  switch (spec.kind) {
    case 'string':
      if (typeof value !== 'string') {
        return false;
      }
      if (spec.value != null && value !== spec.value) {
        return false;
      }
      if (spec.pattern != null && !new RegExp(spec.pattern).test(value)) {
        return false;
      }
      return true;
    case 'number':
      return typeof value === 'number';
    case 'integer':
      if (typeof value !== 'number') {
        return false;
      }
      if (spec.minValue != null && value < spec.minValue) {
        return false;
      }
      if (spec.maxValue != null && value > spec.maxValue) {
        return false;
      }
      return Number.isInteger(value);
    case 'boolean':
      return typeof value === 'boolean';
    case 'color': {
      if (typeof value !== 'string') {
        return false;
      }
      if (spec.pattern != null && !new RegExp(spec.pattern).test(value)) {
        return false;
      }
      return true;
    }
    case 'date':
      return typeof value === 'string' && moment(value, USER_EDIT_DATE_FORMAT, true).isValid();
    case 'columnReference': {
      if (typeof value !== 'string') {
        return false;
      }
      try {
        const columns = (inputDataFrames[spec.inputDataName] ?? []).flatMap(({columns}) => columns);
        const column = findColumn({columns}, value);
        if (column == null) {
          return spec.allowMissingColumn === true;
        }
        if (spec.validationOptions) {
          validateColumnType(column.type, spec.validationOptions);
        }
      } catch (err) {
        if (err instanceof ColumnTypeValidationException) {
          return false;
        }
        throw err;
      }
      return true;
    }
    case 'columnValue': {
      // Column values could have any type, and we don't have access to the underlying data, so don't
      // try to validate
      return true;
    }
    case 'tableReference': {
      if (!Array.isArray(value)) {
        return false;
      }
      if (value.length === 0) {
        return false;
      }
      if (!value.every((row) => typeof row === 'object' && row != null && !Array.isArray(row))) {
        return false;
      }

      const rows = value as Array<Record<string, JSONValue>>;

      const inputDataFrameSpecs = inputDataFrames[spec.inputDataName];
      if (inputDataFrameSpecs == null || inputDataFrameSpecs.length <= spec.inputDataIndex) {
        return false;
      }

      const columns = getUserColumns(inputDataFrameSpecs[spec.inputDataIndex]);

      return rows.every((row) =>
        columns.every(
          (column) =>
            row.hasOwnProperty(column.systemName) &&
            isValidBenchlingTypeValue(row[column.systemName], columnTypeToTypeDef(column.type))
        )
      );
    }
    case 'array': {
      if (!Array.isArray(value)) {
        return false;
      }
      if (spec.minItems != null && value.length < spec.minItems) {
        return false;
      }
      if (spec.maxItems != null && value.length > spec.maxItems) {
        return false;
      }
      return value.every((element) => isValueValid(element, spec.elementSpec, inputDataFrames));
    }
    case 'enum':
      return spec.choices.some((choice) => (typeof choice === 'object' ? choice.value : choice) === value);
    case 'taggedUnion': {
      if (typeof value !== 'object' || value == null || Array.isArray(value)) {
        return false;
      }
      const tagValue = value[spec.tagKey];
      if (tagValue == null) {
        return false;
      }
      const tagSpec = spec.choices.find((choice) => {
        const tagField = choice.fields[spec.tagKey];
        return tagField != null && tagField.kind === 'string' && tagField.value === tagValue;
      });
      if (tagSpec == null) {
        return false;
      }
      return isValueValid(value, tagSpec, inputDataFrames);
    }
    case 'shape':
      if (typeof value !== 'object' || value == null || Array.isArray(value)) {
        return false;
      }
      return Object.entries(spec.fields).every(([fieldName, fieldSpec]) => {
        const fieldValue = value[fieldName];
        return isValueValid(fieldValue, fieldSpec, inputDataFrames);
      });
    case 'domain':
      if (typeof value !== 'string') {
        return false;
      }
      return spec.types.some((type) =>
        new RegExp(`^${getIdPrefixForObjectType(type)}_[A-Za-z0-9]+$`).test(value)
      );
    case 'columnMapping':
      if (typeof value !== 'object' || value == null || Array.isArray(value)) {
        return false;
      }
      return Object.entries(spec.fields).every(([fieldName, fieldSpec]) =>
        isValueValid(value[fieldName], fieldSpec, inputDataFrames)
      );
    case 'schemaFields':
      // An open {fieldSystemName: columnSystemName} map; row targets are resolved server-side, so
      // validate only the shape here (column existence is enforced by the grid's pickers).
      if (typeof value !== 'object' || value == null || Array.isArray(value)) {
        return false;
      }
      return Object.values(value).every((columnName) => typeof columnName === 'string');
    case 'anything':
      return true;
    default:
      assert(false, `Unexpected jsonSpec ${assertNever(spec)}`);
  }
}

/**
 * Get a step type's config shape, which can be used with `isValueValid` to validate a `PipelineStepConfig`.
 */
export function getStepTypeConfigShape(
  stepType: PipelineStepType,
  stepVersion: number
): GetPipelineConfigSpecType<'shape'> {
  return {
    kind: 'shape',
    fields: PIPELINE_STEP_SHAPES[stepType][stepVersion].config ?? {},
    required: true,
  };
}

export function getPipelineStepConfig<StepType extends PipelineStepType, StepVersion extends number>(
  step: GetAnalysisPipelineStep,
  stepType: StepType,
  stepVersion: StepVersion
): PipelineStepConfig<StepType, StepVersion> {
  assert(
    step.definition.stepType === stepType && step.definition.stepVersion === stepVersion,
    'Unexpected step type and version'
  );
  assert(step.config != null, 'Expected non-null step config.');

  return objectFromEntries(
    step.config.variables.map((variable) => [variable.name, variable.value])
  ) as PipelineStepConfig<StepType, StepVersion>;
}
